import { Anchor, Badge, Box, Card, Progress, Table, Text } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Run } from '@/api/runs';
import { rowClick, rowStyle } from '@/components/rowClick';
import { CellText } from '@/components/CellText';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/components/useTableSort';
import { useFormat } from '@/components/useFormat';
import { InternalStateChip } from '@/features/staff/InternalStateChip';
import { brand } from '@/theme/theme';
import { RunErrorLine } from './RunErrorLine';
import { runFailure } from './runError';

/** Seconds a run took, or has taken so far; null before forun started it. */
export function runSeconds(r: Pick<Run, 'started_at' | 'finished_at'>, now: number = Date.now()): number | null {
  if (!r.started_at) return null;
  const end = r.finished_at ? Date.parse(r.finished_at) : now;
  return Math.max(0, Math.round((end - Date.parse(r.started_at)) / 1000));
}

/** "45 s", "12 min", "3 h 05 min", "2 d 4 h": as precise as a glance needs. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}\u00a0s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}\u00a0min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}\u00a0h ${String(minutes % 60).padStart(2, '0')}\u00a0min`;
  return `${Math.floor(hours / 24)}\u00a0d ${hours % 24}\u00a0h`;
}

export function RunsTable({ runs, hideObject = false }: { runs: Run[]; hideObject?: boolean }) {
  const { t, i18n } = useTranslation('runs');
  const lang = i18n.language as 'cs' | 'en';
  const { relative } = useFormat();
  const navigate = useNavigate();
  const { sorted, sortProps } = useTableSort(runs, {
    run: (r) => r.public_id,
    forun: (r) => r.external_id || null,
    analysis: (r) => (r.analysis_name ? r.analysis_name[lang] || r.analysis_name.cs : (r.analysis ?? r.data_object)),
    duration: (r) => runSeconds(r),
    error: (r) => runFailure(r.error)?.code || (r.state === 'failed' ? '—' : null),
    project: (r) => r.project_name,
    type: (r) => r.type,
    state: (r) => r.state,
    progress: (r) => r.progress ?? 0,
    created: (r) => Date.parse(r.created_at),
    author: (r) => r.triggered_by ? r.triggered_by.full_name || r.triggered_by.email : t('system'),
  });
  return (
    <Card p={0} style={{ overflowX: 'auto' }}>
      <Table horizontalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <SortableTh {...sortProps('run')}>{t('columns.run')}</SortableTh>
            <SortableTh {...sortProps('forun')}>{t('columns.forun')}</SortableTh>
            <SortableTh {...sortProps('project')}>{t('columns.project')}</SortableTh>
            {/* A run serves an analysis or preprocesses an object, never both: one column for either. */}
            <SortableTh {...sortProps('analysis')}>{hideObject ? t('columns.analysis') : t('columns.target')}</SortableTh>
            <SortableTh {...sortProps('type')}>{t('columns.type')}</SortableTh>
            <SortableTh {...sortProps('state')}>{t('columns.state')}</SortableTh>
            <SortableTh {...sortProps('error')}>{t('columns.error')}</SortableTh>
            <SortableTh {...sortProps('progress')}>{t('columns.progress')}</SortableTh>
            <SortableTh {...sortProps('duration')}>{t('columns.duration')}</SortableTh>
            <SortableTh {...sortProps('created')}>{t('columns.created')}</SortableTh>
            <SortableTh {...sortProps('author')}>{t('columns.triggeredBy')}</SortableTh>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((r) => (
            <Table.Tr key={r.public_id} style={rowStyle} onClick={rowClick(() => void navigate({ to: '/staff/runs/$runId', params: { runId: r.public_id } }))}>
              <Table.Td>
                <Link to="/staff/runs/$runId" params={{ runId: r.public_id }} style={{ fontFamily: 'monospace', fontSize: 12, color: brand.text }}>
                  {r.public_id}
                </Link>
              </Table.Td>
              <Table.Td>
                {r.external_id ? (
                  r.forun_url ? (
                    <Anchor href={r.forun_url} target="_blank" rel="noopener" fz={12} ff="monospace" style={{ overflowWrap: 'anywhere' }}>
                      #{r.external_id} ↗
                    </Anchor>
                  ) : (
                    <Text fz={12} ff="monospace" style={{ overflowWrap: 'anywhere' }}>
                      #{r.external_id}
                    </Text>
                  )
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                <CellText lines={2} fz={14} miw={90} maw={220} title={r.project_name}>
                  <Link to="/staff/projects/$projectId" params={{ projectId: r.project }} style={{ color: brand.text, textDecoration: 'none' }}>
                    {r.project_name}
                  </Link>
                </CellText>
              </Table.Td>
              <Table.Td>
                {r.analysis ? (
                  <CellText lines={2} fz={13} miw={110} maw={260} title={r.analysis_name ? r.analysis_name[lang] || r.analysis_name.cs : r.analysis}>
                    <Link to="/staff/analyses/$analysisId" params={{ analysisId: r.analysis }} style={{ color: brand.text, textDecoration: 'none' }}>
                      {r.analysis_name ? r.analysis_name[lang] || r.analysis_name.cs : r.analysis}
                      {typeof r.step_index === 'number' && ` · ${t('columns.step', { n: r.step_index + 1 })}`}
                    </Link>
                  </CellText>
                ) : r.data_object && !hideObject ? (
                  <Link to="/staff/objects/$objectId" params={{ objectId: r.data_object }} style={{ fontFamily: 'monospace', fontSize: 12, color: brand.textMuted }}>
                    {r.data_object}
                  </Link>
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                {/* A slug breaks at its hyphens, never inside a word. */}
                <Text fz={13} fw={600}>
                  {r.type}
                </Text>
              </Table.Td>
              <Table.Td>
                <InternalStateChip state={r.state ?? ''} />{' '}
                {r.stalled_at && (
                  <Badge color="red" variant="light" size="xs">
                    {t('states.stalled')}
                  </Badge>
                )}
                {r.superseded_by && (
                  <Badge color="gray" variant="light" size="xs" ml={4}>
                    {t('states.superseded')}
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>
                <Box miw={110} maw={260}>
                  <RunErrorLine run={r} lines={2} />
                </Box>
              </Table.Td>
              <Table.Td w={100}>
                <Progress value={Math.round((r.progress ?? 0) * 100)} color="forest.6" bg="forest.0" size="sm" radius="sm" />
              </Table.Td>
              <Table.Td>
                <Text fz={13} c={brand.textMuted} style={{ whiteSpace: 'nowrap' }}>
                  {r.started_at ? formatDuration(runSeconds(r) ?? 0) : '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz={13} c={brand.textMuted} style={{ whiteSpace: 'nowrap' }}>
                  {relative(r.created_at)}
                </Text>
              </Table.Td>
              <Table.Td>{r.triggered_by ? r.triggered_by.full_name || r.triggered_by.email : t('system')}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
